import { Link } from 'react-router-dom'
import { ChevronLeft, User } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { PoliticalLabel } from '@/components/ideology/PoliticalLabel'
import type { Party } from '@/types'

interface PartyPublicHeaderProps {
  party: Party
  nationName: string
  nationId: string
  ownerUsername?: string
}

export function PartyPublicHeader({
  party,
  nationName,
  nationId,
  ownerUsername,
}: PartyPublicHeaderProps) {
  return (
    <div className="space-y-4">
      {/* Breadcrumb */}
      <div className="flex items-center justify-between">
        <Link
          to={`/nations/${nationId}`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {nationName}
        </Link>
      </div>

      {/* Party identity */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          {/* Color bar */}
          <div
            className="w-2 h-12 rounded-full"
            style={{ backgroundColor: party.color }}
          />

          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold">{party.name}</h1>
              <Badge
                variant="outline"
                style={{
                  borderColor: party.color,
                  color: party.color,
                }}
              >
                {party.abbreviation}
              </Badge>
            </div>
          </div>
        </div>

        {/* Slogan */}
        {party.slogan && (
          <p className="text-muted-foreground italic pl-5">
            "{party.slogan}"
          </p>
        )}

        {/* Political Position Label */}
        <div className="pl-5">
          <PoliticalLabel
            ideology={party.ideology_vector}
            showSubLabel={true}
            size="md"
            variant="card"
          />
        </div>

        {/* Owner and founded info */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground pl-5">
          <div className="flex items-center gap-1">
            <User className="h-4 w-4" />
            <span>Led by: {ownerUsername || 'Unknown'}</span>
          </div>
          <span>•</span>
          <span>Founded: Tick {party.created_tick}</span>
        </div>
      </div>
    </div>
  )
}
